import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useConfirmLoginStore, useResendCodeStore, useStartLoginStore } from '@lib/store';
import { useInterval } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import { Button, TextInput } from '@shared/widgets';

interface ILoginForm {
  username: string;
}
const LOGIN_DEFAULT_VALUES: ILoginForm = { username: '' };
interface IConfirmForm {
  password: string;
  code: string;
}
const CONFIRM_DEFAULT_VALUES: IConfirmForm = { password: '', code: '' };

const StepEnum = {
  Start: 'start',
  Confirmation: 'confirmation',
} as const;
type StepEnum = (typeof StepEnum)[keyof typeof StepEnum];

export const Login = () => {
  const startLoginStore = useStartLoginStore();
  const resendCodeStore = useResendCodeStore();
  const confirmLoginStore = useConfirmLoginStore();

  const [step, setStep] = useState<StepEnum>(StepEnum.Start);
  const [count, setCount] = useState<number>(0);

  const loginForm = useForm({ defaultValues: LOGIN_DEFAULT_VALUES });
  const confirmForm = useForm({ defaultValues: CONFIRM_DEFAULT_VALUES });
  const { t } = useTranslation('login');

  useInterval(() => setCount((prev) => prev - 1), count ? 1000 : null);
  const resendHandler = async () => {
    await resendCodeStore.resendCode();
    setCount(30);
  };
  const loginSubmitHandler = async (data: ILoginForm) => {
    const response = await startLoginStore.startLogin({ username: data.username });
    if (response.error) {
      loginForm.setError('username', { message: response.error });
      return;
    }
    setStep(StepEnum.Confirmation);
    setCount(30);
  };
  const confirmSubmitHandler = async (data: IConfirmForm) => {
    const response = await confirmLoginStore.confirmLogin({ password: data.password, code: data.code });
    if (response.error) confirmForm.setError('password', { message: response.error });
  };

  return (
    <>
      {step === StepEnum.Start && (
        <form className='mb-[68px] max-[840px]:mb-[50px]' autoComplete='off' noValidate onSubmit={loginForm.handleSubmit(loginSubmitHandler)}>
          <Controller
            name='username'
            control={loginForm.control}
            rules={{ required: t('errors.emptyUsername') }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inputClassName={clsx(
                  'hover:border-black transition-all ease duration-300',
                  PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
                )}
                caption={t('placeholders.username', { ns: 'common' })}
                name='username'
                error={error?.message}
              />
            )}
          />
          <Button
            className={clsx('h-[50px] text-base', PaletteUtils.isBordierPalette && ' !border-[#dc291c] hover:!bg-[#dc291c]')}
            fullWidth
            caption={t('buttons.login')}
            type='submit'
            disabled={loginForm.formState.isSubmitting}
          />
        </form>
      )}
      {step === StepEnum.Confirmation && (
        <form className='mb-17' autoComplete='off' onSubmit={confirmForm.handleSubmit(confirmSubmitHandler)}>
          <Controller
            name='password'
            control={confirmForm.control}
            rules={{ required: t('errors.emptyPassword') }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inputClassName={clsx(
                  'hover:border-black transition-all ease duration-300 !text-2xl tracking-[3px]',
                  PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
                )}
                caption={t('placeholders.password', { ns: 'common' })}
                name='password'
                type='password'
                error={error?.message}
              />
            )}
          />
          <Controller
            name='code'
            control={confirmForm.control}
            rules={{ required: t('errors.emptyCode'), minLength: { value: 6, message: t('errors.codeLength') } }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inputClassName={clsx(
                  'hover:border-black transition-all ease  duration-300 !text-2xl tracking-[3px]',
                  PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
                )}
                caption={t('placeholders.confirmationCode', { ns: 'common' })}
                name='code'
                inputPattern='\d'
                maxLength={6}
                error={error?.message}
              />
            )}
          />
          <Button
            className={clsx('mx-auto my-2.5 mb-7.5 h-[50px] text-sm font-medium')}
            disabled={count > 0 || resendCodeStore.loading}
            caption={
              <span>
                {t('buttons.resend', { ns: 'common' })}
                {count > 0 && <span className='text-inherit'>{` (${count})`}</span>}
              </span>
            }
            onClick={resendHandler}
          />
          <Button className='h-[50px] text-base' fullWidth caption={t('buttons.login')} type='submit' disabled={confirmForm.formState.isSubmitting} />
        </form>
      )}
      <Link
        className={clsx(
          'mb-4 font-light text-base whitespace-nowrap text-left uppercase',
          PaletteUtils.isBordierPalette ? 'hover:!text-[#dc291c] tracking-[1.2px]' : ' text-[#dadada] hover:text-[#e5c496] tracking-[1.7px]',
        )}
        to='/remember'
      >
        {t('links.forgotPassword')}
      </Link>
      <Button className='!h-[40px] leading-5' caption={t('buttons.register')} url='/register' />
    </>
  );
};
